import { InputWithLabel } from '@adotel/components'
import { Checkbox, FormGroup } from '@material-ui/core'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import IconButton from '@material-ui/core/IconButton'
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer'
import Typography from '@material-ui/core/Typography'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import { Rating } from '@material-ui/lab'
import React, { useState, FC, useEffect } from 'react'

import CustomCheckBox from '../../../../components/CustomCheckBox'
import { convertPersianNumbersToEnglishNumbers } from '../../../../helpers/numbers'
import { getTranslate, lang } from '../../../../localization'
import { ResourceType } from '../../../../types/accommodation'
import { CheckBoxDataType } from '../../../../types/customCheckBox'
import {
  Entities,
  SearchAvailabilityInput
} from '../../../../types/searchAvailability'
import ContentFitList from '../../../BookingHotelSearchResult/components/ContentFitList'
import PriceSlider from '../../../BookingHotelSearchResult/components/PriceSlider'
import useStyles from './styles/index.styles'

interface FilterDrawerProps {
  open: boolean
  onClose: () => void
  onOpen: () => void
  data: Entities[]
  isUserPriceChange: React.MutableRefObject<boolean>
  priceRange: number[]
  maxPriceRange: number
  searchAvailabilityInput: SearchAvailabilityInput
  setPriceRange: React.Dispatch<React.SetStateAction<number[]>>
  selectedHotelStar: number[]
  setselectedHotelStar: React.Dispatch<React.SetStateAction<number[]>>
  selectedFacilities: string[]
  setSelectedFacilities: React.Dispatch<React.SetStateAction<string[]>>
  selectedPropertyType: string[]
  setSelectedPropertyType: React.Dispatch<React.SetStateAction<string[]>>
  setsearchInputVal: React.Dispatch<React.SetStateAction<string>>
}

const FilterDrawer: FC<FilterDrawerProps> = ({
  open,
  onClose,
  onOpen,
  searchAvailabilityInput,
  priceRange,
  maxPriceRange,
  setPriceRange,
  isUserPriceChange,
  data,
  selectedHotelStar,
  selectedFacilities,
  setSelectedFacilities,
  selectedPropertyType,
  setSelectedPropertyType,
  setsearchInputVal,
  setselectedHotelStar
}) => {
  const classes = useStyles()
  const [initialFacilities, setInitialFacilities] = useState<
    CheckBoxDataType[] | null
  >(null)
  const propertyTypeOptions: ResourceType[] = ['Hotel', 'Motel', 'BNB']
  const min = 0
  const handleHotelStarchecked = (value: number) => {
    setselectedHotelStar((prev) => {
      const updated = prev.includes(value)
        ? prev.filter((v) => v !== value)
        : [...prev, value]

      return updated
    })
  }
  const handlePriceChange = (newRange: number[]) => {
    isUserPriceChange.current = true
    setPriceRange(newRange)
  }

  useEffect(() => {
    if (!initialFacilities && data.length > 0) {
      const hotelsFacilities: { label: string; code: string }[] = []
      data?.forEach((entity) => {
        entity.entityBasicDetails.facilities.forEach((f) =>
          hotelsFacilities.push({
            code: f.ID,
            label: f.Name
          })
        )
      })

      const seen = new Set<string>()
      const uniqueHotelsFacilities: CheckBoxDataType[] = hotelsFacilities
        .filter((hf) => {
          if (seen.has(hf.code)) {
            return false
          } else {
            seen.add(hf.code)
            return true
          }
        })
        .map((d) => ({
          title: d.label,
          value: d.code,
          checked: false
        }))

      setInitialFacilities(uniqueHotelsFacilities)
    }
  }, [data, initialFacilities])
  const uniqueHotelsFacilities = initialFacilities || []

  const handleResetAll = () => {
    setSelectedFacilities([])
    setSelectedPropertyType([])
    setselectedHotelStar([])
    setPriceRange([min, maxPriceRange])
  }

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={onOpen}
      dir={lang === 'fa' ? 'rtl' : 'ltr'}
      classes={{ paper: classes.drawerPaper }}>
      <div className={classes.root}>
        <div className={classes.header}>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
          <Typography variant="h4" className={classes.filterTitle}>
            {getTranslate('Filters')}
          </Typography>
          <Typography className={classes.clearBtn} onClick={handleResetAll}>
            {getTranslate('Reset All')}
          </Typography>
        </div>
        <InputWithLabel
          placeholder={getTranslate('Enter Hotel Name')}
          inputSize="small"
          onChange={(event) => {
            setsearchInputVal(
              convertPersianNumbersToEnglishNumbers(event.currentTarget.value)
            )
          }}
          startIcon={<SearchIcon />}
        />
        <PriceSlider
          label={getTranslate('Price Range For 1 Night')}
          currency={searchAvailabilityInput.currency}
          min={min}
          max={maxPriceRange}
          price={priceRange}
          setPrice={handlePriceChange}
        />
        <div>
          <Typography variant="h5" className={classes.filterText}>
            {getTranslate('Star Rating')}
          </Typography>
          <FormGroup className={classes.ratingForm}>
            {[5, 4, 3, 2, 1].map((ele, idx) => (
              <div className={classes.starRow} key={idx}>
                <Rating value={ele} max={ele} readOnly size="small" />
                <FormControlLabel
                  label=""
                  classes={{ root: classes.check }}
                  control={
                    <Checkbox
                      color="primary"
                      size="small"
                      className={classes.checkbox}
                      value={selectedHotelStar.includes(ele)}
                      checked={selectedHotelStar.includes(ele)}
                      onChange={() => handleHotelStarchecked(ele)}
                    />
                  }
                />
              </div>
            ))}
          </FormGroup>
        </div>
        <ContentFitList
          header={getTranslate('Property Type')}
          options={propertyTypeOptions.map((r) => ({
            value: r,
            label: r
          }))}
          selectedItems={selectedPropertyType}
          setSelectedItems={setSelectedPropertyType}
        />
        {uniqueHotelsFacilities.length > 0 && (
          <CustomCheckBox
            header={getTranslate('Facilities')}
            options={uniqueHotelsFacilities}
            selectedItems={selectedFacilities}
            setSelectedItems={setSelectedFacilities}
          />
        )}
      </div>
    </SwipeableDrawer>
  )
}

export default FilterDrawer
